import {Component, Vue} from "vue-property-decorator";
import {mapActions, mapGetters} from "vuex";
import gql from "graphql-tag";
import uniq from "array-uniq";
import {AxiosInstance} from "axios";
import {HdsActions} from "@/store/modules/hds/actions";
import {DataPropertyProfile} from "@/store/modules/hcs/dataPropertyProfile/model";
import {DataPropertyImplementation} from "@/store/modules/hcs/dataPropertyImplementation/model";
import {DataPropertyDefinition} from "@/store/modules/hds/Model";

@Component({
  computed: {
    ...mapGetters({
      _hcsClient: 'hcs/client',
    }),
  },
  methods: {
    ...mapActions({
      _gqlQuery: 'hds/gqlQuery',
    }),
  }
})
export default class extends Vue {
  /** hcs/hcsClient */
  readonly _hcsClient!: AxiosInstance;

  /** hds/gqlQuery */
  readonly _gqlQuery!: HdsActions['gqlQuery'];

  /**
   * データ項目プロファイルを読み込みます
   *
   * @param dataProfileId データプロファイルID
   */
  async loadDataPropertyProfiles(dataProfileId: string) {
    const {data: dataPropertyImplementations} = await this._hcsClient.get<DataPropertyImplementation[]>(`/data_property_implementations`);
    const dataPropertyProfiles = (await this._hcsClient.get<DataPropertyProfile[]>(`/data_property_profiles`, {
      params: {'q[data_profile_id_eq]': dataProfileId}
    }))
        .data
        .map(dpp => ({
          ...dpp,
          dataPropertyImplementation: {
              ...dpp?.dataPropertyImplementation,
              ...dataPropertyImplementations.find(dpi => dpi.id === dpp.dataPropertyImplementationId)! // eslint-disable-line
          }
        }));

    return this.bindDataPropertyDefinitions(dataPropertyProfiles);
  }

  /**
   * 他事業者のデータ項目プロファイルを読み込みます
   *
   * @param dataProfileId データプロファイルID
   */
  async loadAssetsDataPropertyProfiles(dataProfileId: string) {
    const {data: dataPropertyProfiles} = await this._hcsClient.get(`/assets/data_property_profiles`, {
      params: {'q[data_profile_id_eq]': dataProfileId}
    });

    return this.bindDataPropertyDefinitions(dataPropertyProfiles);
  }

  /**
   * データ項目プロファイルにデータ項目定義を追加します
   *
   * @param dataPropertyProfiles データ項目プロファイルの配列
   * @private
   */
  private async bindDataPropertyDefinitions(dataPropertyProfiles: (DataPropertyProfile & {dataPropertyImplementation: DataPropertyImplementation})[]) {
    // データ項目プロファイルに紐付く共通辞書
    const commonDataPropertyDefinitionIds = uniq(
        dataPropertyProfiles
            .map(dpp => dpp.commonDataPropertyDefinitionId)
            .filter(dpdId => !!dpdId)
    );

    // データ項目実装に紐付く個別辞書
    const localPropertyDefinitionIds = uniq(([] as string[]).concat(
        dataPropertyProfiles
            .map(dpp => dpp.dataPropertyImplementation?.dataPropertyDefinitionId)
            .filter(dpdId => !!dpdId),
        dataPropertyProfiles
            .map(dpp => dpp.localDataPropertyDefinitionId)
            .filter(dpdId => !!dpdId)
    ));

    // データ項目定義の取得
      const {commonDataPropertyDefinitions, localDataPropertyDefinitions} = await this._gqlQuery({
          query: gql`
              query FindDataPropertyDefinition(
                  $commonDataPropertyDefinitionIds: [ID!]!,
                  $localPropertyDefinitionIds: [ID!]!,
              ) {
                  commonDataPropertyDefinitions: dataPropertyDefinitionInBulk(dataPropertyDefinitionIds: $commonDataPropertyDefinitionIds) {
                      id
                      name
                      description
                      dataType
                      isPrimaryKey
                      isRequired
                  }
                  localDataPropertyDefinitions: dataPropertyDefinitionInBulk(dataPropertyDefinitionIds: $localPropertyDefinitionIds) {
                      id
                      name
                      description
                      dataType
                      isPrimaryKey
                      isRequired
                  }
              }`,
        variables: {
          commonDataPropertyDefinitionIds, localPropertyDefinitionIds
        }
      });

    return dataPropertyProfiles.map(dpp => {
      const dpi = dpp.dataPropertyImplementation;
      const commonDataPropertyDefinition = commonDataPropertyDefinitions.find((dpd: DataPropertyDefinition) => dpd.id === dpp.commonDataPropertyDefinitionId);
      const localDataPropertyDefinition = localDataPropertyDefinitions.find((dpd: DataPropertyDefinition) => dpd.id === dpi.dataPropertyDefinitionId);
      return {
        ...dpp,
        commonDataPropertyDefinition,
        localDataPropertyDefinition,
      } as DataPropertyProfile & {
        commonDataPropertyDefinition: DataPropertyDefinition;
        localDataPropertyDefinition: DataPropertyDefinition;
      };
    });
  }
}
